import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PAYMENT } from "@/constants/testIds";

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ loading: true });
  const { clearCart } = useApp();

  useEffect(() => {
    if (!sessionId) { setStatus({ loading: false, error: "No session id" }); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setStatus({ loading: false, paid: true, data: r.data });
          clearCart();
          return;
        }
        if (attempts >= 20) {
          setStatus({ loading: false, paid: false, data: r.data });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setStatus({ loading: false, error: "Could not verify" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clearCart]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      {status.loading && (
        <>
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-primary" strokeWidth={1.5} />
          <p className="mt-6 text-sm uppercase tracking-label text-muted-foreground">Verifying your offering…</p>
        </>
      )}
      {!status.loading && status.paid && (
        <div data-testid={PAYMENT.successStatus}>
          <CheckCircle2 className="w-14 h-14 mx-auto text-primary" strokeWidth={1.5} />
          <h1 className="display-serif text-4xl mt-6">Blessings received.</h1>
          <p className="mt-3 text-muted-foreground">Payment of ${status.data.amount?.toFixed?.(2)} confirmed. Your sacred goods will ship soon.</p>
          <Link to="/" data-testid={PAYMENT.homeLink} className="inline-block mt-10 px-6 py-3 bg-primary text-primary-foreground text-xs uppercase tracking-label font-bold luxe-btn">Return home</Link>
        </div>
      )}
      {!status.loading && !status.paid && (
        <div data-testid={PAYMENT.cancelStatus}>
          <XCircle className="w-14 h-14 mx-auto text-destructive" strokeWidth={1.5} />
          <h1 className="display-serif text-4xl mt-6">Payment not yet confirmed</h1>
          <p className="mt-3 text-muted-foreground">{status.error || "We will keep watching — refresh in a moment or contact support."}</p>
          <Link to="/" data-testid={PAYMENT.homeLink} className="inline-block mt-10 px-6 py-3 border border-foreground text-xs uppercase tracking-label font-bold luxe-btn">Return home</Link>
        </div>
      )}
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid={PAYMENT.cancelStatus}>
    <XCircle className="w-14 h-14 mx-auto text-muted-foreground" strokeWidth={1.5} />
    <h1 className="display-serif text-4xl mt-6">Payment cancelled</h1>
    <p className="mt-3 text-muted-foreground">Your basket is intact — return whenever you are ready.</p>
    <Link to="/store" data-testid={PAYMENT.homeLink} className="inline-block mt-10 px-6 py-3 bg-primary text-primary-foreground text-xs uppercase tracking-label font-bold luxe-btn">Back to store</Link>
  </div>
);
